import React, { createRef } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import toast from 'react-hot-toast';
import { Input } from '../../../components';
import { classNames, clone, Constants, delay, formatMiles, fromStorage, round } from '../../../utils';
import { CloseIcon, ReportIcon, ListIcon } from '../../../assets/icons';
import { PaymentsService, PurchasesService } from '../../../services';

const TYPE = {
  DONE: 1,
  FEE: 2,
  ANALIZE: 3,
};

const getStatusColor = (status, type) => {
  if (type === TYPE.ANALIZE) {
    switch (status) {
      case Constants.PURCHASE.STATUS.ANALIZE:
        return 'bg-orange';
      case Constants.PURCHASE.STATUS.APPROVED_ANALYSIS:
        return 'bg-violet';
      default:
        return 'bg-transparent';
    }
  }

  if (type === TYPE.FEE)
    return 'bg-transparent';

  switch (status) {
    case Constants.PAYMENT.STATUS.PENDING:
      return 'bg-yellow';
    case Constants.PAYMENT.STATUS.APPROVED:
      return 'bg-green';
    case Constants.PAYMENT.STATUS.REJECTED:
      return 'bg-red';
    default:
      return 'bg-transparent';
  }
};

const getStatusName = (status) => {
  switch (status) {
    case Constants.PAYMENT.STATUS.PENDING:
      return 'En Proceso';
    case Constants.PAYMENT.STATUS.REJECTED:
      return 'No procedente';
    default:
      return '';
  }
};

const getReadableStatus = (cutoffDate) => {
  const daysDiff = moment(cutoffDate).diff(moment(moment().format('YYYY-MM-DD')), 'days');
  if (daysDiff < 1) return 'Vencida';
  if (daysDiff === 1) return 'Vence mañana';
  if (daysDiff <= 5) return `Vence en ${daysDiff} días`;
  return 'Por vencer';
};

const getReadableStatusColor = (cutoffDate) => {
  const daysDiff = moment(cutoffDate).diff(moment(moment().format('YYYY-MM-DD')), 'days');
  if (daysDiff < 1) return 'text-red';
  if (daysDiff === 1) return 'text-red';
  if (daysDiff <= 5) return 'text-red';
  return 'text-blue';
};

const INITIAL_FILTERS = {
  since: '',
  until: '',
  page: 1,
  pages: 1,
};

class Payments extends React.Component {

  state = {
    isLoading: false,
    form: { ...INITIAL_FILTERS },
    payments: [],
    selectedProduct: null,
    fees: [],
  }

  feesModalRef = createRef();

  componentDidMount() {
    this.initialize();
  }

  initialize = () => {
    try {
      Promise.all([
        PurchasesService.getPurchasesToPay(),
        PurchasesService.getProductsToPay(),
        PaymentsService.get(this.state.form),
      ])
        .then(res => {
          const unPaidPurchase = res[0].data.map(x => ({
            ...x,
            subtotal: x.price,
            iva: 0,
            price: x?.price,
            _type: TYPE.ANALIZE,
          }));

          const unPaidFee = res[1].data.map(x => ({
            ...x,
            subtotal: x.price,
            iva: 0,
            price: x.price,
            _type: TYPE.FEE,
          }));

          const paid = res[2].data.map(x => ({ ...x, _type: TYPE.DONE }));
          this.setState({
            payments: [ ...unPaidPurchase, ...unPaidFee, ...paid ],
            form: {
              ...this.state.form,
              pages: res[2].pages,
            },
          });
        })
        .catch(() => toast.error('Ocurrió un error inesperado'));

    } catch (error) {
      console.log(error);
    }
  }

  getPayments = async (page = 1) => {
    this.setState({ isLoading: true });
    const { form } = this.state;

    try {
      const data = {
        ...form,
        page,
        since: form.since ? moment(form.since).format('YYYY-MM-DD') : '',
        until: form.until ? moment(form.until).format('YYYY-MM-DD') : '',
      };

      const res = await PaymentsService.get(data);
      const payments = res.data.map(x => ({ ...x, _type: TYPE.DONE }));

      this.setState({
        payments: page > 1 ? [ ...this.state.payments, ...payments ] : payments,
        form: {
          ...form,
          pages: res.pages,
          page,
        },
      });

    } catch (error) {
      console.log(error);
      toast.error('Ocurrió un error inesperado');
    }

    this.setState({ isLoading: false });
  }

  change = async (value,target) => {
    await this.setState({
      form: {
        ...this.state.form,
        [target]: value,
      },
    });

    this.getPayments();
  }

  goToPay = async (item) => {
    const request = {
      paymentType: Constants.PAYMENT.TYPE.FEE,
      purchaseProductId: item.id,
      subtotal: round(item.subtotal),
      iva: round(item.iva),
      total: round(item.price),
      fees_qty: 1,
    };

    this.props.dispatch({
      type: 'SET_REQUEST',
      payload: { ...request, purchase: request }
    });
    await delay(10);
    this.props.history.push('/pagar');
  }

  goToPayFees = async () => {
    const { selectedProduct } = this.state;
    if (!selectedProduct) return;

    const payingFees = this.state.fees.filter(x => x.isPayable && x.isSelected).length;

    const request = {
      paymentType: Constants.PAYMENT.TYPE.FEE,
      purchaseProductId: selectedProduct.id,
      subtotal: round(selectedProduct.subtotal * payingFees),
      iva: round(selectedProduct.iva * payingFees),
      total: round(selectedProduct.price * payingFees),
      fees_qty: payingFees,
    };

    this.props.dispatch({
      type: 'SET_REQUEST',
      payload: { ...request, purchase: request }
    });
    await delay(10);
    this.props.history.push('/pagar');
    this.feesModalRef.current?.close();
    this.setState({ fees: [] });
  }

  goToPayAll = async () => {
    const { selectedProduct } = this.state;
    if (!selectedProduct) return;

    const request = {
      paymentType: Constants.PAYMENT.TYPE.FEE,
      purchaseProductId: selectedProduct.id,
      subtotal: round(selectedProduct.subtotal * selectedProduct.pending_fees),
      iva: round(selectedProduct.iva * selectedProduct.pending_fees),
      total: round(selectedProduct.price * selectedProduct.pending_fees),
      fees_qty: selectedProduct.pending_fees,
    };

    this.props.dispatch({
      type: 'SET_REQUEST',
      payload: { ...request, purchase: request }
    });
    await delay(10);
    this.props.history.push('/pagar');
    this.feesModalRef.current?.close();
    this.setState({ fees: [] });
  }

  canCleanFilters = () => {
    const current = this.state.form;
    const initial = INITIAL_FILTERS;
    return current.since !== initial.since || current.until !== initial.until;
  }

  cleanFilters = async () => {
    await this.setState({ form: { ...INITIAL_FILTERS } });
    await delay(1);
    this.initialize();
  }

  downloadPayment = async (item) => {
    try {
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
      const res = await PaymentsService.download(item.id);
      const el = document.createElement('a');
      el.href = fromStorage(res.url);
      el.download = `Pago_${String(item.id).padStart(4,'0')}`;
      el.target =  iOS ? '_self' : '_blank';
      el.click();
      el.remove();

    } catch (error) {
      console.log(error);
    }
  }

  showFees = (type, item) => {
    const initialDate = moment(moment(item.created_at).format('YYYY-MM-DD'));
    const currentFee = item.fees - item.pending_fees;
    const fees = [];

    const _getStatus = (fee, date) => {
      if (fee <= currentFee) {
        return {
          label: 'Pagada',
          color: 'text-green',
          isPayable: false,
        };
      }

      return {
        label: getReadableStatus(date),
        color: getReadableStatusColor(date),
        isPayable: true,
      };
    };

    for (let fee = 1; fee <= item.fees; fee++) {
      const date = initialDate.clone().add(fee - 1, 'months');
      const status = _getStatus(fee, date);

      fees.push({
        feeNumber: `${fee}/${item.fees}`,
        date: date.format('DD-MM-YYYY'),
        status: status.label,
        statusColor: status.color,
        amount: item.price,
        isPayable: status.isPayable,
        isSelected: false,
      });
    }

    this.setState({ selectedProduct: { ...item, _type: type}, fees });
    this.feesModalRef.current?.showModal();
  }

  onToggleSelectToPay = (index) => {
    const fees = clone(this.state.fees.map(x => ({ ...x, isSelected: false })));
    const firstToPayIdx = this.state.fees.findIndex(x => x.isPayable);
    if (firstToPayIdx === -1) return;

    if (index === firstToPayIdx) {
      if (!this.state.fees[index].isSelected || this.state.fees.filter(x => x.isSelected).length > 1) {
        fees[index].isSelected = true;
      }

    } else {
      for (let i = firstToPayIdx; i <= index; i++) {
        fees[i].isSelected = true;
      }
    }
    this.setState({ fees });
  }

  goToCompleteData = (item) => {
    this.props.history.push({
      pathname: '/historial/pagos/completar',
      state: {
        item
      }
    });
  }

  render() {

    const { fees, form, payments, selectedProduct } = this.state;
    const canCleanFilters = this.canCleanFilters();
    const payingFees = fees?.filter(x => x.isPayable && x.isSelected)?.length;

    return (
      <>
        <dialog
          ref={this.feesModalRef}
          className="fixed inset-0 z-50 overflow-y-auto bg-transparent"
        >
          <div className="w-full sm:w-[28rem] bg-white rounded-xl overflow-hidden p-4 flex flex-col items-center">
            <div className="w-full">
              <button
                className="absolute top-3 right-3"
                onClick={() => {
                  this.feesModalRef.current?.close();
                  this.setState({ fees: [] });
                }}
              >
                <img src={CloseIcon} className="w-4 h-4 object-contain" alt="" />
              </button>
              <p className="font-bold text-lg text-darkBlue">
                Detalles de Cuotas del Producto
              </p>
            </div>

            <div className="w-full my-4">
              <div className="w-full grid grid-cols-4 gap-x-2 border-b border-b-gray6 font-semibold">
                <p className="text-left">Fecha</p>
                <p className="px-2 sm:p-0">Cuota</p>
                <p>Estatus</p>
                <p className="text-right">Monto</p>
              </div>
              {fees.map((item, idx) => (
                <div key={idx} className="w-full grid grid-cols-4 gap-x-2 items-center py-1 border-b border-b-gray5 text-sm">
                  <div className="flex items-center gap-x-2">
                    {item.isPayable ? (
                      <button
                        className={classNames('w-[18px] aspect-square rounded-md border-2', item.isSelected ? 'bg-green border-green' : 'bg-transparent border-gray6')}
                        onClick={() => this.onToggleSelectToPay(idx)}
                      />
                    ) : (
                      <div className="w-4" />
                    )}
                    <p>{ item?.date }</p>
                  </div>
                  <p className="px-2 sm:p-0">{ item?.feeNumber }</p>
                  <p className={classNames('font-semibold', item?.statusColor)}>
                    { item?.status }
                  </p>
                  <p className="text-right">{ formatMiles(item?.amount) }</p>
                </div>
              ))}
            </div>

            {(selectedProduct?._type === TYPE.FEE && fees.some(x => x.isPayable) && !payingFees) && (
              <div className="flex justify-center mt-2">
                <button
                  className="ml-0 bg-green px-4 py-1 rounded-full font-semibold text-white"
                  onClick={this.goToPayAll}
                >
                  <span>Pagar todo</span>
                </button>
              </div>
            )}

            {(selectedProduct?._type === TYPE.FEE && fees.some(x => x.isPayable) && payingFees > 0) && (
              <div className="flex justify-center mt-2">
                <button
                  className="ml-0 bg-green px-4 py-1 rounded-full font-semibold text-white"
                  onClick={this.goToPayFees}
                >
                  <span>Pagar { payingFees } {  payingFees > 1 ? 'cuotas' : 'cuota' }</span>
                </button>
              </div>
            )}
          </div>
        </dialog>

        <div className="flex items-center gap-4 mb-8 mt-8 lg:mt-0">
          <div className="w-full sm:w-40">
            <Input
              type="date"
              placeholder="Desde"
              value={form.since}
              onValueChange={v => this.change(v,'since')}
            />
          </div>
          <div className="w-full sm:w-40">
            <Input
              type="date"
              placeholder="Hasta"
              value={form.until}
              onValueChange={v => this.change(v,'until')}
            />
          </div>
          {canCleanFilters && (
            <button onClick={this.cleanFilters} className="text-left">
              <span>Limpiar filtros</span>
            </button>
          )}
        </div>

        <div className="max-w-[600px] mx-auto flex flex-col gap-4">
          {payments.map((item, index) => (
            <RequestItem
              key={index}
              item={item}
              onPay={() => this.goToPay(item)}
              onGenerateReceipt={() => this.downloadPayment(item)}
              onShowFees={this.showFees}
              goToCompleteData={ (item) => this.goToCompleteData(item) }
            />
          ))}

          {this.state.form.page < this.state.form.pages && (
            <div className="flex justify-center mt-2">
              <button
                className="ml-0 bg-green px-4 py-1 rounded-full font-semibold text-white"
                onClick={() => this.getPayments(this.state.form.page + 1)}
              >
                <span>Cargar más</span>
              </button>
            </div>
          )}

          {!payments.length && (
            <p className="font-semibold text-gray3 text-center my-10">
              No hay pagos
            </p>
          )}
        </div>
      </>
    );
  }
}

const RequestItem = ({ item, onPay, onGenerateReceipt, onShowFees, goToCompleteData }) => {
  let productName = '';
  let beneficiary = '';
  let amount = 0;
  let feeNumber = '';
  let paidAt = '';
  let expiredAt = '';
  let statusColor = getStatusColor(item.status, item._type);
  let statusName = getStatusName(item.status);

  if (item._type === TYPE.DONE) {
    const isFeeBased = item?.total_fees > 1;
    productName = item.purchase_product?.product?.name || item.unique_products;
    beneficiary = item.purchase_product?.user?.fullName || item.unique_beneficiaries;
    paidAt = moment(item.created_at).format('DD-MM-YYYY');
    amount = item?.amount;
    feeNumber = isFeeBased
      ? `${item?.current_fee + item?.fees_qty - 1} de ${item.total_fees}`
      : 'Pagado';
    expiredAt = moment(item.cutoff_at).format('DD-MM-YYYY');

  } else if (item._type === TYPE.ANALIZE) {
    productName = item.product?.name;
    beneficiary = item.user?.fullName;
    amount = item.price;

    if (item.status === Constants.PURCHASE.STATUS.APPROVED_ANALYSIS) {
      paidAt = moment(item.created_at).format('DD-MM-YYYY');
      feeNumber = `${item?.fees - item?.pending_fees + 1} de ${item.fees}`;
      expiredAt = moment(item.cutoff_at).format('DD-MM-YYYY');

    } else {
      paidAt = false;
      feeNumber = false;
      expiredAt = false;
      statusName = 'Por análisis';
    }

  } else {
    productName = item.product?.name;
    beneficiary = item.user?.fullName;
    paidAt = moment(item.cutoff_at).format('DD-MM-YYYY');
    amount = item?.price;
    feeNumber = `${item?.fees - item?.pending_fees + 1} de ${item.fees}`;
    expiredAt = moment(item.cutoff_at).format('DD-MM-YYYY');
  }

  const amountLabel = [TYPE.FEE, TYPE.ANALIZE].includes(item._type) ? 'Monto a pagar' : 'Monto pagado';

  const show_bravealo = item.purchase?.purchase_products?.map(i => i.req_docs.length).reduce((a,b) => a + b,0) > 0;

  return (
    <div className="bg-white rounded-lg border border-gray6 p-2 text-sm sm:text-base">
      <div className="flex justify-between items-start mb-2">
        <p className="font-bold text-darkBlue flex-1">
          { productName }
        </p>
        <div className={classNames('px-2 py-1 rounded-full text-sm', statusColor)}>
          {item._type === TYPE.FEE && (
            <p className="text-red font-semibold">
              { getReadableStatus(item.cutoff_at) }
            </p>
          )}
          {item._type === TYPE.DONE && (
            <p className="text-white font-semibold" >
              { statusName || item.status_text }
            </p>
          )}
          {item._type === TYPE.ANALIZE && (
            <p className="text-white font-semibold" >
              { statusName || item.status_text }
            </p>
          )}
        </div>
      </div>
      <p className="text-text">
        <span className="font-semibold">Beneficiario: </span>
        <span>{ beneficiary }</span>
      </p>
      {!!paidAt && (
        <p className="text-text">
          <span className="font-semibold">Fecha de pago: </span>
          <span>{ paidAt }</span>
        </p>
      )}
      <p className="text-text">
        <span className="font-semibold">{ amountLabel }: </span>
        <span>{ formatMiles(amount) }</span>
      </p>
      {!!feeNumber && (
        <p className="text-text">
          <span className="font-semibold">N° de cuota: </span>
          <span>{ feeNumber }</span>
        </p>
      )}
      {!!expiredAt && (
        <p className="text-text">
          <span className="font-semibold">Fecha de corte: </span>
          <span>{ expiredAt }</span>
        </p>
      )}

      {
        show_bravealo && item?.face_identity_token !== undefined && item?.vehicle_data == null && item.status == Constants.PAYMENT.STATUS.APPROVED && (
          <button
            className="bg-yellow rounded-xl mt-1 py-2 px-2 font-semibold text-black flex items-center"
            onClick={() => {
              goToCompleteData(item);
            }}
          >
            <div className="rounded-xl w-6 h-6 flex justify-center items-center">
              <img src={ ListIcon } className="w-6 h-6 object-contain" alt="" />
            </div>
            <span className="ml-2">Completa los datos para activar tu RCV</span>
          </button>
        )
      }

      <div className="flex justify-end items-center">
        {item._type === TYPE.DONE && (
          <div className="flex flex-1 justify-between items-center mt-2">
            <div>
              {!!item?.purchase_product_id && (
                <button
                  className="bg-yellow px-4 py-1 rounded-full font-semibold text-black"
                  onClick={() => onShowFees(TYPE.DONE, item?.purchase_product)}
                >
                  <span>Ver cuotas</span>
                </button>
              )}
            </div>

            <button onClick={() => onGenerateReceipt()}>
              <img src={ReportIcon} className="w-5 h-5 object-contain" alt="" />
            </button>
          </div>
        )}

        {(item._type === TYPE.ANALIZE && item.status === Constants.PURCHASE.STATUS.APPROVED_ANALYSIS)  && (
          <div className="flex flex-1 justify-between items-center mt-2">
            <button
              className="bg-yellow px-4 py-1 rounded-full"
              onClick={() => onShowFees(TYPE.FEE, item)}
            >
              <p className="font-semibold text-black">Ver cuotas</p>
            </button>

            <button
              className="bg-green px-4 py-1 rounded-full font-semibold text-white"
              onClick={() => onPay()}
            >
              <span>Pagar</span>
            </button>
          </div>
        )}

        {item._type === TYPE.FEE && (
          <div className="flex flex-1 justify-between items-center mt-2">
            <button
              className="bg-yellow px-4 py-1 rounded-full"
              onClick={() => onShowFees(TYPE.FEE, item)}
            >
              <p className="font-semibold text-black">Ver cuotas</p>
            </button>

            <button
              className="bg-green px-4 py-1 rounded-full font-semibold text-white"
              onClick={() => onPay()}
            >
              <span>Pagar</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default connect(state => ({
  request: state.request,
}))(Payments);
